import { useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useSettings } from "../lib/agent";
import { handOffToChat } from "../lib/chat-handoff";
import { IconChat, IconSearch } from "./icons";
import { cx } from "./ui";

type Destination =
  | "/"
  | "/discussion"
  | "/approbations"
  | "/missions"
  | "/veilles"
  | "/memoire"
  | "/coffre"
  | "/connexions"
  | "/ordinateur"
  | "/agent"
  | "/reglages"
  | "/agents"
  | "/activite";

const PAGES: { to: Destination; label: string; words: string }[] = [
  { to: "/", label: "Aujourd'hui", words: "accueil pulse brief journée" },
  { to: "/discussion", label: "Discussion", words: "message conversation chat" },
  { to: "/approbations", label: "Validations", words: "accord règles valider" },
  { to: "/missions", label: "Missions", words: "tâches" },
  { to: "/veilles", label: "Veilles", words: "surveiller alerte" },
  { to: "/memoire", label: "Mémoire", words: "souvenirs sait" },
  { to: "/coffre", label: "Coffre", words: "mots de passe cartes" },
  { to: "/connexions", label: "Connexions", words: "imessage telegram apps icloud notifications" },
  { to: "/ordinateur", label: "Ordinateur", words: "écran machine" },
  { to: "/agent", label: "Ton agent", words: "visage personnalité" },
  { to: "/reglages", label: "Réglages", words: "rythme thème langue compte" },
  { to: "/agents", label: "Mes agents", words: "plusieurs" },
  { to: "/activite", label: "Activité", words: "coût dépenses journal" },
];

function fold(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** « Rechercher » (⌘ K): jump to a page, or hand the words to the agent. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const settings = useSettings();
  const agentName = settings.data?.agentName ?? "ton agent";
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const inputId = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setQuery("");
      setIndex(0);
      d.showModal();
    } else if (!open && d.open) d.close();
  }, [open]);

  const q = fold(query.trim());
  const pages = useMemo(() => (q ? PAGES.filter((p) => fold(`${p.label} ${p.words}`).includes(q)) : PAGES), [q]);
  const options = [...pages.map((p) => ({ kind: "page" as const, ...p })), ...(query.trim() ? [{ kind: "ask" as const, to: "/discussion" as const, label: `Demander à ${agentName} : « ${query.trim()} »`, words: "" }] : [])];

  function choose(i: number) {
    const o = options[i];
    if (!o) return;
    if (o.kind === "ask") handOffToChat(query, true);
    onClose();
    void navigate({ to: o.to });
  }

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      aria-label="Rechercher"
      className="m-auto mt-[12vh] w-[min(560px,calc(100vw-2.5rem))] rounded-[22px] border border-line bg-elev p-0 text-fg backdrop:bg-[var(--dim)]"
    >
      <div className="flex items-center gap-2.5 border-b border-sunken px-4">
        <IconSearch width={18} height={18} strokeWidth={2} className="shrink-0 text-muted" />
        <label htmlFor={inputId} className="sr-only">
          Rechercher
        </label>
        <input
          id={inputId}
          autoFocus
          value={query}
          placeholder="Rechercher"
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            setIndex(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setIndex((i) => Math.min(i + 1, options.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              choose(index);
            }
          }}
          className="h-13 min-w-0 flex-1 bg-transparent text-[16px] outline-none placeholder:text-subtle"
        />
      </div>
      <ul className="max-h-[50vh] overflow-y-auto p-2">
        {options.map((o, i) => (
          <li key={`${o.kind}-${o.to}`}>
            <button
              type="button"
              onMouseEnter={() => setIndex(i)}
              onClick={() => choose(i)}
              className={cx("flex min-h-11 w-full items-center gap-3 rounded-[10px] px-3 text-left text-[14.5px]", i === index ? "bg-hover font-semibold" : "font-medium")}
            >
              {o.kind === "ask" ? <IconChat width={18} height={18} className="shrink-0 text-accent" /> : null}
              <span className="min-w-0 truncate">{o.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </dialog>
  );
}
